import {Customer} from "../../../model/customer/customer";
import {Injectable} from "@angular/core";
import {CCRoles} from "../../../model/security/cc-roles-type";
import {CustomerComponent} from "../../../crystal-clear/customer/customer/customer.component";
import {FormFieldConfig} from "../../../model/api/form-field-config";
import {FormFieldGroupConfig} from "../../../model/api/form-field-group-config";
import {Trader} from "../../../model/customer/trader";
import {FieldRecord} from "../../../model/api/field-record";
import {CustomerCustomerDataLoadingService} from "./customer-customer-data-loading.service";
import TYPE = FormFieldConfig.TYPE;

@Injectable({
  providedIn: 'root'
})
export class CustomerCustomerFormConfigService {

  constructor(private dataLoadingService: CustomerCustomerDataLoadingService) {
  }

  public loadConfig(component: CustomerComponent): FormFieldGroupConfig[] {
    const groupMainFields: FormFieldConfig[] = [
      {
        field: 'ownerId',
        required: true,
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        options: () => this.dataLoadingService.owners,
        changed: event => {
          component.formRecord.ownerId = parseInt(event.value);
          component.formRecord.owner = component.findOptText(this.dataLoadingService.owners, event.value)
          this.dataLoadingService.refreshBrokerageTables(component.formRecord);
        }
      },
      {
        field: 'code',
        required: true,
        column: 4,
        type: TYPE.TEXT,
        focused: true
      },
      {
        field: 'name',
        required: true,
        column: 5,
        type: TYPE.TEXT
      }];
    if (component.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
      groupMainFields.push(
        {
          field: 'desk',
          column: 3,
          type: TYPE.TEXT
        },
        {
          field: 'salesMan',
          column: 3,
          type: TYPE.TEXT
        });
    }
    groupMainFields.push(
      {
        field: 'language',
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        icon: id => id ? ('flag-icon flag-icon-' + id.toLowerCase()) : '',
        options: () => this.dataLoadingService.languages,
        changed: event => component.formRecord.language = event.value.toUpperCase()
      },
      {
        field: 'country',
        filter: true,
        column: 3,
        type: TYPE.DROPDOWN,
        icon: id => id ? ('flag-icon flag-icon-' + id.toLowerCase()) : '',
        options: () => this.dataLoadingService.countries,
        changed: event => component.formRecord.country = event.value.toUpperCase()
      });

    if (component.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
      groupMainFields.push(
        {
          field: 'email',
          column: 12,
          type: TYPE.TEXT
        },
        {
          field: 'confirmationLayout',
          column: 4,
          type: TYPE.TEXT
        },
        {
          field: 'lei',
          column: 3,
          type: TYPE.TEXT
        });
    }
    groupMainFields.push(
      {
        field: 'enabled',
        column: 3,
        type: TYPE.CHECKBOX
      });
    const formConfig: FormFieldGroupConfig[] = [
      {
        group: '',
        fields: groupMainFields
      }];
    if (component.hasRoles(CCRoles.ROLES_CUSTOMER_WRITE_TRADER)) {
      formConfig.push(this.buildTradersConfig(component));
    }
    if (component.hasRoles(CCRoles.ROLES_CUSTOMER_READ_ADVANCED)) {
      formConfig.push({
          group: 'address',
          fields:
            [{
              field: 'address',
              column: 12,
              type: TYPE.TEXT
            },
              {
                field: 'zipCode',
                column: 2,
                type: TYPE.TEXT
              },
              {
                field: 'town',
                column: 5,
                type: TYPE.TEXT
              },
              {
                field: 'stateAddress',
                column: 5,
                type: TYPE.TEXT
              },
            ]
        },
        {
          group: 'invoice_detail',
          fields: [
            {
              field: 'currency',
              column: 5,
              type: TYPE.DROPDOWN,
              filter: true,
              options: () => this.dataLoadingService.currencies,
              icon: id => 'cc-ico',
              iconValue: id => {
                const curr = this.dataLoadingService.currencies && this.dataLoadingService.currencies.find(value => value.id === id) ?
                  this.dataLoadingService.currencies.find(value => value.id === id) : undefined;
                if (curr)
                  return curr.value;
                return '';
              }
            },
            {
              field: 'billingDueNbDays',
              column: 2,
              type: TYPE.INTEGER
            },
            {
              field: 'bankAccountId',
              column: 5,
              type: TYPE.DROPDOWN,
              options: () => this.dataLoadingService.bankAccounts
            },
            {
              field: 'invoiceMail',
              column: 12,
              type: TYPE.TEXT
            },
            {
              field: 'vat',
              column: 3,
              type: TYPE.CHECKBOX
            },
            {
              field: 'vatNumber',
              column: 3,
              type: TYPE.TEXT
            },
            {
              field: 'brokerageTableName',
              column: 3,
              type: TYPE.DROPDOWN,
              options: () => this.dataLoadingService.brokerageTablesVisibles
            },
            {
              field: 'invoiceTemplate',
              column: 3,
              type: TYPE.DROPDOWN,
              options: () => this.dataLoadingService.invoices
            },
          ]
        });
    }
    return formConfig;
  }

  private buildTradersConfig(component: CustomerComponent): any {
    return {
      group: 'traders',
      // collapsed: true,
      fields: [
        {
          field: 'traders',
          class: 'with-title height-limited filter-table',
          column: 12,
          type: TYPE.TABLEFORMFIELD,
          richFieldConfig: {
            fields: [
              {
                group: '',
                fields: [
                  {
                    field: 'code',
                    label: '&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;Code',
                    type: TYPE.AUTOCOMPLETE,
                    // optionValue: 'id',

                    options: () => this.dataLoadingService.traders.filter(trader => !component.formRecord.traders.some(addedT => addedT.code === trader.id)),
                    leftButton: {
                      icon: 'fa-solid fa-ban',
                      command: (record) => {
                        this.deleteTrader(component, record);
                      },
                      class: 'button-field-small'
                    },
                    changed: (event: FieldRecord<Trader>, event2: Trader) => {
                      if (event && event.value) {
                        event2.initials = event.value.initials
                        event2.description = event.value.description
                        component.crudTable.reloadFieldValueMap();
                      }
                    }
                  },
                  {
                    field: 'initials',
                    label: 'Initials',
                    type: TYPE.TEXT,
                  },
                  {
                    field: 'description',
                    label: 'Description',
                    type: TYPE.TEXT,
                    // rightButton: {
                    //   icon: 'fa-solid fa-ban',
                    //   command: (record) => {
                    //     this.deleteTrader(component, record);
                    //   },
                    //   class: 'button-field-small'
                    // }
                  }]
              },
            ]
          },
        }, {
          column: 12,
          type: TYPE.BUTTONS,
          class: 'button-add',
          buttons: [{
            icon: 'fas fa-trash-can',
            class: 'p-button-warning',
            command: event => {
              this.deleteTrader(component);
            },
            disabledFct: record => {
              return !component.formRecord || !component.formRecord.traders || component.formRecord.traders.length == 0;
            }
          },
            {
              icon: 'fas fa-plus',
              class: 'p-button-success',
              command: event => {
                this.addNewTrader(component.formRecord);
                component.crudTable.reloadFieldValueMap();
              },
              disabledFct: record => {
                return !component.formRecord || component.formRecord.traders &&
                  (component.formRecord.traders.findIndex((trader: Trader) => trader.code == '') >= 0
                    || new Set(component.formRecord.traders.map(t => t.code)).size !== component.formRecord.traders.length);
              }
            }]
        }]
    };
  }

  private deleteTrader(component: CustomerComponent, trader?: Trader) {
    component.confirmDialog(() => {
      this.delete(component.formRecord, trader);
      component.crudTable.reloadFieldValueMap();
    }, " ", trader ? "Are you sure you want to delete this trader?<br/>This action will only remove the trader from this customer."
      : "Are you sure you want to delete all the traders?<br/>This action will only remove the traders from this customer.");
  }

  private addNewTrader(formRecord: Customer) {
    if (formRecord) {
      if (!formRecord.traders) {
        formRecord.traders = [];
      }
      formRecord.traders.push({
        code: "",
        description: "",
        initials: ""
      });
    }
  }

  private delete(formRecord: Customer, record?: Trader) {
    if (formRecord) {
      if (record) {
        const existingValueIndex: number = formRecord.traders.findIndex(value => value.code == record.code);
        if (existingValueIndex >= 0) {
          formRecord.traders.splice(existingValueIndex, 1);
        }
      } else {
        formRecord.traders.splice(0);
      }
    }
  }

}
